import React, { useContext, useEffect, useState, useRef, useCallback } from 'react';
import {
  IInvoice,
  ICompany,
  useDeleteInvoiceMutation,
  useInvoicesFilteredLazyQuery,
  useCompaniesQuery,
  useInvoiceCreatedSubscription,
  useInvoiceUpdatedSubscription,
  useInvoiceDeletedSubscription
} from '../../generated/graphql';
import { AppContext, InvoiceProductInfo } from '../../AppContext';
import { calculateDiscountPercent, calculateVat } from '../../utils/mathUtils';
import { Box, Button, ButtonGroup, Chip } from '@mui/material';
import OverviewTable, { IHeadCell } from '../Common/Tables/OverviewTable';
import moment from 'moment';
import { buttons, Colors } from '../../utils/theme';
import { DATE_TIME_FORMATS, INVOICE_STATUS_CONFIRMED } from '../../utils/constants';
import { Actions } from '../../enums/ActionEnums';
import { removeInvoiceFromCache } from '../../utils/clientUtils';
import Loader from '../Common/Loader';
import InvoiceStepper from './Wizard/InvoiceStepper';
import AutocompleteComponent from '../Common/Fields/AutocompleteComponent';
import InputComponent from '../Common/Fields/InputComponent';
import CancelButton from '../Common/Buttons/CancelButton';

export const getInvoiceProductInfoDto = (invoice: IInvoice): InvoiceProductInfo => {
  return {
    discount: invoice.discount ?? 0,
    discountPercent: calculateDiscountPercent(invoice.product.price, invoice.discount),
    vatPercent: invoice.vat ?? null,
    vat: calculateVat(invoice.product.price, invoice.discount, invoice.vat),
    product: invoice.product,
    total: invoice.total
  };
};

const ProductCodes = {
  AC: 'AC',
  BPR: 'BPR',
  RB: 'RB'
};

const groupButtonWidth = '90px';

const InvoiceList: React.FunctionComponent = () => {
  const { dispatch } = useContext(AppContext);
  const companyData = useCompaniesQuery();

  // Filter states
  const [pending, setPending] = useState(false);
  const [dueAndUnpaid, setDueAndUnpaid] = useState(false);
  const [number, setNumber] = useState<string>('');
  const [company, setCompany] = useState<ICompany | null>(null);
  const [contactName, setContactName] = useState<string>('');
  const [city, setCity] = useState<string>('');
  const [invoiceYears, setInvoiceYears] = useState<number[]>([]);
  const [ac, setAc] = useState(false);
  const [bpr, setBpr] = useState(false);
  const [rb, setRb] = useState(false);

  // Pagination states
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(15);
  const [total, setTotal] = useState(0);

  // Sorting states
  const [orderBy, setOrderBy] = useState<string | null>(null);
  const [orderDirection, setOrderDirection] = useState<'asc' | 'desc'>('asc');

  // Local state for displayed invoices (updated by query and subscriptions)
  const [displayedInvoices, setDisplayedInvoices] = useState<IInvoice[]>([]);

  const [yearsOptions, setYearsOptions] = useState<number[]>([]);
  const [openWizard, setOpenWizard] = useState(false);
  const [deleteInvoice, deleteInvoiceResult] = useDeleteInvoiceMutation();

  const [fetchInvoices, { data, loading, refetch }] = useInvoicesFilteredLazyQuery();

  // Subscriptions for real-time updates
  const createInvoiceSubscription = useInvoiceCreatedSubscription();
  const updateInvoiceSubscription = useInvoiceUpdatedSubscription();
  const deleteInvoiceSubscription = useInvoiceDeletedSubscription();

  // Refs for tracking state
  const hasMounted = useRef(false);
  const filterTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isFilterChangeRef = useRef(false);
  const isInitialMount = useRef(true);
  const lastPageRef = useRef(page);
  const lastPageSizeRef = useRef(pageSize);

  useEffect(() => {
    const now = new Date();
    const years = [];
    for (let index = 2010; index <= now.getFullYear(); index++) {
      years.push(index);
    }
    setYearsOptions(years);
  }, []);

  // Helper function to check if an invoice matches current filters
  const invoiceMatchesFilters = useCallback(
    (invoice: IInvoice): boolean => {
      // Check pending filter
      if (pending && invoice.status?.name !== 'Pending') {
        return false;
      }

      // Check due and unpaid filter
      if (dueAndUnpaid) {
        const now = new Date();
        const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        const dueDate = invoice.due_date ? new Date(parseInt(invoice.due_date as string)) : null;
        if (!dueDate || invoice.date_paid !== null || dueDate >= today) {
          return false;
        }
      }

      // Check number filter
      if (number && number.trim().length > 0) {
        const invoiceNumber = invoice.number?.toLowerCase() || '';
        if (!invoiceNumber.includes(number.trim().toLowerCase())) {
          return false;
        }
      }

      // Check company filter
      if (company && company.id != null) {
        const invoiceCompanyName = invoice.details?.company_name?.toLowerCase() || '';
        const companyName = company.name?.toLowerCase() || '';
        if (!invoiceCompanyName.includes(companyName)) {
          return false;
        }
      }

      // Check contact name filter
      if (contactName && contactName.trim().length > 0) {
        const firstName = invoice.details?.contact_first_name?.toLowerCase() || '';
        const lastName = invoice.details?.contact_last_name?.toLowerCase() || '';
        const fullName = `${firstName} ${lastName}`.trim();
        if (!fullName.includes(contactName.trim().toLowerCase())) {
          return false;
        }
      }

      // Check city filter
      if (city && city.trim().length > 0) {
        const invoiceCity = invoice.details?.city?.toLowerCase() || '';
        if (!invoiceCity.includes(city.trim().toLowerCase())) {
          return false;
        }
      }

      // Check years filter
      if (invoiceYears.length > 0) {
        const invoiceYear = invoice.created_date
          ? new Date(parseInt(invoice.created_date as string)).getFullYear()
          : null;
        if (!invoiceYear || !invoiceYears.includes(invoiceYear)) {
          return false;
        }
      }

      // Check product codes filter
      const selectedProductCodes: string[] = [];
      if (ac) selectedProductCodes.push(ProductCodes.AC);
      if (bpr) selectedProductCodes.push(ProductCodes.BPR);
      if (rb) selectedProductCodes.push(ProductCodes.RB);
      if (selectedProductCodes.length > 0) {
        const invoiceProductCode = invoice.product?.product_code?.name;
        if (!invoiceProductCode || !selectedProductCodes.includes(invoiceProductCode)) {
          return false;
        }
      }

      return true;
    },
    [pending, dueAndUnpaid, number, company, contactName, city, invoiceYears, ac, bpr, rb]
  );

  useEffect(() => {
    if (data?.result) {
      const newTotal = data.result.total || 0;
      setTotal(newTotal);
      // Filter out null/undefined values from the invoices array
      const validInvoices: IInvoice[] = (data.result.invoices || []).filter(
        (invoice): invoice is IInvoice => invoice !== null && invoice !== undefined
      );
      setDisplayedInvoices(validInvoices);

      // Only reset page if it's actually invalid (only when filters change and we get fewer results)
      if (newTotal > 0) {
        const maxPage = Math.max(0, Math.ceil(newTotal / pageSize) - 1);
        if (page > maxPage && isFilterChangeRef.current) {
          setPage(0);
        }
      } else if (newTotal === 0 && page > 0 && isFilterChangeRef.current) {
        setPage(0);
      }
    }
  }, [data, pageSize, page]);

  // Handle subscription events - update local state directly
  useEffect(() => {
    if (!hasMounted.current) {
      return;
    }

    if (createInvoiceSubscription.data?.result) {
      const newInvoice = createInvoiceSubscription.data.result as IInvoice;

      // Check if new invoice matches current filters
      if (invoiceMatchesFilters(newInvoice)) {
        setDisplayedInvoices((prev) => {
          // Only add if it doesn't already exist
          const exists = prev.some((i) => i.id === newInvoice.id);
          if (!exists) {
            setTotal((currentTotal) => currentTotal + 1);
            return [newInvoice, ...prev];
          }
          return prev;
        });
        setPage(0);
      }
    }

    if (updateInvoiceSubscription.data?.result) {
      const updatedInvoice = updateInvoiceSubscription.data.result as IInvoice;

      setDisplayedInvoices((prev) => {
        return prev.map((i) => (i.id === updatedInvoice.id ? updatedInvoice : i));
      });
    }

    if (deleteInvoiceSubscription.data?.result?.id) {
      const deletedInvoiceId = deleteInvoiceSubscription.data.result.id;

      setDisplayedInvoices((prev) => {
        const invoiceToRemove = prev.find((i) => i.id === deletedInvoiceId);
        if (invoiceToRemove) {
          removeInvoiceFromCache(invoiceToRemove);
          setTotal((currentTotal) => Math.max(0, currentTotal - 1));
          return prev.filter((i) => i.id !== deletedInvoiceId);
        }
        return prev;
      });
    }
  }, [
    createInvoiceSubscription.data,
    updateInvoiceSubscription.data,
    deleteInvoiceSubscription.data,
    invoiceMatchesFilters
  ]);

  // Initial load - only run once on mount
  useEffect(() => {
    if (!hasMounted.current) {
      hasMounted.current = true;
      // Initial load with default filter
      const filter: any = {
        limit: pageSize,
        offset: 0
      };
      // Add sorting parameters if set
      if (orderBy) {
        filter.orderBy = orderBy;
        filter.orderDirection = orderDirection;
      }
      fetchInvoices({
        variables: { filter }
      });
    }
  }, [fetchInvoices, pageSize]);

  // Debounce filter changes to avoid too many requests
  useEffect(() => {
    // Skip if this is the initial mount
    if (!hasMounted.current) {
      return;
    }

    // Clear any existing timeout
    if (filterTimeoutRef.current) {
      clearTimeout(filterTimeoutRef.current);
    }

    isFilterChangeRef.current = true;

    filterTimeoutRef.current = setTimeout(() => {
      // Reset to first page when filters change and reload
      setPage(0);

      // Build filter object directly here
      const filter: any = {
        limit: pageSize,
        offset: 0
      };

      if (pending) filter.pending = true;
      if (dueAndUnpaid) filter.dueAndUnpaid = true;
      if (number && number.trim().length > 0) filter.number = number.trim();
      if (contactName && contactName.trim().length > 0) filter.contact_name = contactName.trim();
      if (city && city.trim().length > 0) filter.city = city.trim();
      if (invoiceYears.length > 0) filter.years = invoiceYears;
      if (company && company.name) {
        filter.company_name = company.name.trim();
      }

      // Add product codes filter
      const selectedProductCodes: string[] = [];
      if (ac) selectedProductCodes.push(ProductCodes.AC);
      if (bpr) selectedProductCodes.push(ProductCodes.BPR);
      if (rb) selectedProductCodes.push(ProductCodes.RB);
      if (selectedProductCodes.length > 0) {
        filter.productCodes = selectedProductCodes;
      }

      // Add sorting parameters
      if (orderBy) {
        filter.orderBy = orderBy;
        filter.orderDirection = orderDirection;
      }

      fetchInvoices({
        variables: { filter }
      });

      // Reset flag after a short delay
      setTimeout(() => {
        isFilterChangeRef.current = false;
      }, 100);
    }, 500); // 500ms debounce

    return () => {
      if (filterTimeoutRef.current) {
        clearTimeout(filterTimeoutRef.current);
      }
    };
  }, [
    pending,
    dueAndUnpaid,
    number,
    company,
    contactName,
    city,
    invoiceYears,
    ac,
    bpr,
    rb,
    pageSize,
    orderBy,
    orderDirection,
    fetchInvoices
  ]);

  // Reload when sorting changes
  const lastOrderByRef = useRef(orderBy);
  const lastOrderDirectionRef = useRef(orderDirection);

  useEffect(() => {
    // Skip if this is the initial mount or if values haven't changed
    if (!hasMounted.current) {
      lastOrderByRef.current = orderBy;
      lastOrderDirectionRef.current = orderDirection;
      return;
    }

    const isOrderByChange = lastOrderByRef.current !== orderBy;
    const isOrderDirectionChange = lastOrderDirectionRef.current !== orderDirection;

    if (isOrderByChange || isOrderDirectionChange) {
      // Build filter object with current filters and sorting
      const filter: any = {
        limit: pageSize,
        offset: 0 // Reset to first page when sorting changes
      };

      if (pending) filter.pending = true;
      if (dueAndUnpaid) filter.dueAndUnpaid = true;
      if (number && number.trim().length > 0) filter.number = number.trim();
      if (contactName && contactName.trim().length > 0) filter.contact_name = contactName.trim();
      if (city && city.trim().length > 0) filter.city = city.trim();
      if (invoiceYears.length > 0) filter.years = invoiceYears;
      if (company && company.name) {
        filter.company_name = company.name.trim();
      }

      // Add product codes filter
      const selectedProductCodes2: string[] = [];
      if (ac) selectedProductCodes2.push(ProductCodes.AC);
      if (bpr) selectedProductCodes2.push(ProductCodes.BPR);
      if (rb) selectedProductCodes2.push(ProductCodes.RB);
      if (selectedProductCodes2.length > 0) {
        filter.productCodes = selectedProductCodes2;
      }

      // Add sorting parameters
      if (orderBy) {
        filter.orderBy = orderBy;
        filter.orderDirection = orderDirection;
      }

      setPage(0); // Reset to first page
      fetchInvoices({
        variables: { filter }
      });

      lastOrderByRef.current = orderBy;
      lastOrderDirectionRef.current = orderDirection;
    }
  }, [
    orderBy,
    orderDirection,
    pageSize,
    pending,
    dueAndUnpaid,
    number,
    company,
    contactName,
    city,
    invoiceYears,
    ac,
    bpr,
    rb,
    fetchInvoices
  ]);

  // Reload when pagination changes (user clicks next/previous)
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      lastPageRef.current = page;
      lastPageSizeRef.current = pageSize;
      return;
    }

    // Check if this is a page change or pageSize change (not a filter change)
    const isPageChange = lastPageRef.current !== page;
    const isPageSizeChange = lastPageSizeRef.current !== pageSize;

    // If filters just changed and we're on page 0, skip this effect
    if (isFilterChangeRef.current && page === 0 && !isPageSizeChange) {
      lastPageRef.current = page;
      lastPageSizeRef.current = pageSize;
      return;
    }

    // Normal pagination change - fetch the new page explicitly
    if (isPageChange || isPageSizeChange) {
      const filter: any = {
        limit: pageSize,
        offset: page * pageSize
      };

      if (pending) filter.pending = true;
      if (dueAndUnpaid) filter.dueAndUnpaid = true;
      if (number && number.trim().length > 0) filter.number = number.trim();
      if (contactName && contactName.trim().length > 0) filter.contact_name = contactName.trim();
      if (city && city.trim().length > 0) filter.city = city.trim();
      if (invoiceYears.length > 0) filter.years = invoiceYears;
      if (company && company.name) {
        filter.company_name = company.name.trim();
      }

      // Add product codes filter
      const selectedProductCodes4: string[] = [];
      if (ac) selectedProductCodes4.push(ProductCodes.AC);
      if (bpr) selectedProductCodes4.push(ProductCodes.BPR);
      if (rb) selectedProductCodes4.push(ProductCodes.RB);
      if (selectedProductCodes4.length > 0) {
        filter.productCodes = selectedProductCodes4;
      }

      // Add sorting parameters
      if (orderBy) {
        filter.orderBy = orderBy;
        filter.orderDirection = orderDirection;
      }

      fetchInvoices({
        variables: { filter }
      });

      lastPageRef.current = page;
      lastPageSizeRef.current = pageSize;
    }
  }, [
    page,
    pageSize,
    pending,
    dueAndUnpaid,
    number,
    company,
    contactName,
    city,
    invoiceYears,
    ac,
    bpr,
    rb,
    orderBy,
    orderDirection,
    fetchInvoices
  ]);

  const handlePageChange = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newPageSize = parseInt(event.target.value, 10);
    setPageSize(newPageSize);
    setPage(0);
  };

  const handleSortChange = useCallback(
    (newOrderBy: string | null, newOrderDirection: 'asc' | 'desc') => {
      setOrderBy(newOrderBy);
      setOrderDirection(newOrderDirection);
      setPage(0);
    },
    []
  );

  const formatDate = (rowData: IInvoice, name: string) => {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    return rowData[name]
      ? // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        moment(parseInt(rowData[name] as string)).format(DATE_TIME_FORMATS.DATE)
      : '';
  };

  const renderStatus = (status: string) => {
    return (
      <p style={{ color: status === INVOICE_STATUS_CONFIRMED ? Colors.green : Colors.red }}>
        {status}
      </p>
    );
  };

  const handlePendingPresses = () => {
    setPending(!pending);
    setDueAndUnpaid(false);
  };

  const handleDueAndUnpaidPressed = () => {
    setDueAndUnpaid(!dueAndUnpaid);
    setPending(false);
  };

  const handleProductCodePressed = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    const { name } = event.currentTarget;
    if (name === ProductCodes.AC) {
      setAc(!ac);
    } else if (name === ProductCodes.BPR) {
      setBpr(!bpr);
    } else if (name === ProductCodes.RB) {
      setRb(!rb);
    }
  };

  const handleYearChange = (_: unknown, years: number[]) => {
    setInvoiceYears(years);
  };

  const resetFilters = () => {
    setPending(false);
    setDueAndUnpaid(false);
    setNumber('');
    setCompany(null);
    setContactName('');
    setCity('');
    setInvoiceYears([]);
    setAc(false);
    setBpr(false);
    setRb(false);
  };

  const handleDetails = (rowData: IInvoice) => {
    setOpenWizard(true);
    dispatch({
      type: Actions.SetInvoice,
      payload: {
        invoice: rowData,
        productInfo: getInvoiceProductInfoDto(rowData)
      }
    });
    dispatch({
      type: Actions.SetSelectedMembership,
      payload: {
        id: rowData.membership?.id as string,
        name: rowData.membership?.name as string,
        start_date: rowData.membership?.start_date ?? null,
        end_date: rowData.membership?.end_date ?? null
      }
    });
  };

  const removeInvoice = (rowData: IInvoice) =>
    dispatch({
      type: Actions.ShowConfirmation,
      payload: {
        text: `Are you sure you want to delete current pending invoice?`,
        agreeAction: handleDelete,
        params: Number(rowData.id)
      }
    });

  const handleDelete = (id: number) => {
    deleteInvoice({
      variables: {
        id
      },
      update() {
        const invoiceToDelete = displayedInvoices.find((i) => i.id === id.toString());
        removeInvoiceFromCache(invoiceToDelete as IInvoice);
        dispatch({
          type: Actions.ShowMessage,
          payload: { text: `Invoice deleted ${id}`, severity: 'success' }
        });
      }
    })
      .catch((e) => {
        dispatch({
          type: Actions.ShowMessage,
          payload: {
            severity: 'error',
            text: e.message,
            autoHide: 6000
          }
        });
      })
      .finally(() => {
        dispatch({
          type: Actions.HideConfirmation
        });
      });
  };

  const columns = [
    {
      title: 'Details',
      width: 63,
      sorting: false
    },
    {
      title: 'Number',
      field: 'number',
      width: 122
    },
    {
      title: 'Date',
      field: 'created_date',
      width: 97,
      type: 'date',
      render: (rowData: IInvoice) => formatDate(rowData, 'created_date')
    },
    {
      title: 'Due',
      field: 'due_date',
      width: 97,
      type: 'date',
      render: (rowData: IInvoice) => formatDate(rowData, 'due_date')
    },
    {
      title: 'Date Paid',
      field: 'due_date',
      width: 97,
      type: 'date',
      render: (rowData: IInvoice) => formatDate(rowData, 'date_paid')
    },
    {
      title: 'Status',
      field: 'status.name',
      width: 97,
      type: 'date',
      render: (rowData: IInvoice) => renderStatus(rowData.status.name)
    },
    {
      title: 'Invoice Total',
      field: 'total',
      type: 'numeric',
      width: 69
    },
    {
      title: 'Company',
      field: 'details.company_name',
      width: 231
    },
    {
      title: 'Contact',
      width: 180,
      customSort: (a: IInvoice, b: IInvoice) => {
        const firstContactName = `${a.details?.contact_first_name ?? ''} ${
          a.details?.contact_last_name ?? ''
        }`;
        const secondContactName = `${b.details?.contact_first_name ?? ''} ${
          b.details?.contact_last_name ?? ''
        }`;
        return firstContactName.localeCompare(secondContactName);
      },
      render: (rowData: IInvoice) =>
        `${rowData.details?.contact_first_name ?? ''} ${rowData.details?.contact_last_name ?? ''}`
    },
    {
      title: 'Street',
      field: 'details.street',
      width: 231
    },
    {
      title: 'Product Code',
      field: 'product.product_code.name',
      width: 70
    },
    {
      title: 'City',
      field: 'details.city',
      width: 166
    },
    {
      title: 'Remove',
      width: 73,
      sorting: false
    }
  ] as IHeadCell[];

  const companies = companyData.data?.result || [];

  return (
    <Box className="overviewPage" sx={{ height: '100%' }}>
      <OverviewTable
        columns={columns}
        data={displayedInvoices}
        loading={loading}
        handleDetails={handleDetails}
        handleRemove={removeInvoice}
        hideSearch={true}
        additionalFilters={
          <Box id="filters" sx={{ display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
            <Button
              id="pending-filter-button"
              data-testid="pending-filter-button"
              variant="contained"
              onClick={handlePendingPresses}
              sx={pending ? buttons.selected : buttons.deSelected}
              color="primary"
              name="pending"
            >
              Pending
            </Button>
            <Button
              id="due-and-unpaid-filter-button"
              data-testid="due-and-unpaid-filter-button"
              variant="contained"
              sx={dueAndUnpaid ? buttons.selected : buttons.deSelected}
              onClick={handleDueAndUnpaidPressed}
              color="primary"
              name="dueAndUnpaid"
            >
              Due & Unpaid
            </Button>
            <ButtonGroup color="inherit" aria-label="outlined primary button group">
              <Button
                id="product-code-ac-filter-button"
                data-testid="product-code-ac-filter-button"
                variant="contained"
                sx={
                  ac
                    ? { ...buttons.selected, width: groupButtonWidth }
                    : { ...buttons.deSelected, width: groupButtonWidth }
                }
                onClick={handleProductCodePressed}
                color="primary"
                name={ProductCodes.AC}
              >
                AC
              </Button>
              <Button
                id="product-code-bpr-filter-button"
                data-testid="product-code-bpr-filter-button"
                variant="contained"
                sx={
                  bpr
                    ? { ...buttons.selected, width: groupButtonWidth }
                    : { ...buttons.deSelected, width: groupButtonWidth }
                }
                onClick={handleProductCodePressed}
                color="primary"
                name={ProductCodes.BPR}
              >
                BPR
              </Button>
              <Button
                id="product-code-rb-filter-button"
                data-testid="product-code-rb-filter-button"
                variant="contained"
                sx={
                  rb
                    ? { ...buttons.selected, width: groupButtonWidth }
                    : { ...buttons.deSelected, width: groupButtonWidth }
                }
                onClick={handleProductCodePressed}
                color="primary"
                name={ProductCodes.RB}
              >
                RB
              </Button>
            </ButtonGroup>
            <InputComponent
              id="invoice-number-filter"
              label="Number"
              value={number}
              onChange={(e) => setNumber(e.target.value)}
              additionalStyle={{ width: '200px' }}
            />
            <AutocompleteComponent
              id="company-filter"
              label="Company"
              values={company}
              options={companies}
              onChange={(_: unknown, newValue: ICompany | null) => setCompany(newValue)}
              getOptionLabel={(option: ICompany) => option.name || ''}
              isSingle={true}
              additionalStyle={{ width: '200px' }}
            />
            <InputComponent
              id="contact-name-filter"
              label="Contact Name"
              value={contactName}
              onChange={(e) => setContactName(e.target.value)}
              additionalStyle={{ width: '200px' }}
            />
            <InputComponent
              id="city-filter"
              label="City"
              value={city}
              onChange={(e) => setCity(e.target.value)}
              additionalStyle={{ width: '200px' }}
            />
            <AutocompleteComponent
              id="invoice-year-label"
              data-testid="invoice-year-label"
              label="Invoice Year"
              values={invoiceYears}
              options={yearsOptions}
              isSingle={false}
              onChange={handleYearChange}
              getOptionLabel={(option: number) => option.toString()}
              additionalStyle={{ width: '200px' }}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => {
                  const { key, ...tagProps } = getTagProps({ index });
                  return (
                    <Chip
                      key={option}
                      sx={{
                        backgroundColor: (t) => t.palette.grey[50],
                        borderColor: Colors.skyBlue,
                        height: '24px',
                        color: Colors.main,
                        '& .MuiChip-deleteIcon': {
                          width: '20px',
                          height: '20px',
                          color: Colors.skyBlue,
                          '&:hover': {
                            color: Colors.main
                          }
                        }
                      }}
                      variant="outlined"
                      label={option}
                      onMouseDown={(event) => {
                        event.stopPropagation();
                      }}
                      {...tagProps}
                    />
                  );
                })
              }
            />
            <CancelButton
              id="clear-filter-button"
              data-testid="clear-filter-button"
              onClick={resetFilters}
              text="clear all"
              sx={{
                textDecoration: 'underline',
                '&:hover': {
                  textDecoration: 'underline',
                  background: (theme) => theme.palette.primary.main,
                  color: Colors.white,
                  border: 'none'
                }
              }}
            />
          </Box>
        }
        page={page}
        onPageChange={handlePageChange}
        onRowsPerPageChange={handleRowsPerPageChange}
        totalCount={total}
        pageSize={pageSize}
        orderBy={orderBy}
        orderDirection={orderDirection}
        onSortChange={handleSortChange}
      />
      {openWizard && <InvoiceStepper onClose={() => setOpenWizard(false)} />}
      <Loader loading={deleteInvoiceResult.loading} />
    </Box>
  );
};

export default InvoiceList;
